/**
 * @file apps/web/src/lib/approvalCards.ts
 * @description Pure logic behind the client portal's approval cards, pulled out
 * of the components so it can be unit-tested without a DOM. Fixes four things
 * that were wrong on a client approver's card:
 *
 *   - "DELEGATED" on a step nobody delegated. The approvals LIST endpoint builds
 *     each step without a `delegated_to` field, so the web code saw `undefined`
 *     and `undefined !== null` read as "delegated". A step is delegated only when
 *     `delegated_to` actually holds an id.
 *   - A title built as "{entity_type} approval" ("fdd approval approval"). The
 *     approval has a real title; use it.
 *   - Grouping: only approvals with status 'pending' counted as open, so a
 *     running ('in_progress') approval fell under "Completed". Open approvals are
 *     split into "waiting for you" and "in progress"; only finished ones are
 *     "completed".
 *   - No way to read the document being approved. An approval is matched to its
 *     document so the card can show its title and link to it.
 */

export interface ApprovalStepLike {
  approver_id:   string;
  decision:      string;
  delegated_to?: string | null;
  activated_at?: string | null;
  skipped_at?:   string | null;
}

export interface ApprovalLike {
  id:           string;
  status:       string;
  entity_type:  string;
  entity_id:    string;
  title?:       string | null;
  document_id?: string | null;
  steps?:       ApprovalStepLike[];
}

/** Statuses of an approval that is still running (not yet approved/rejected/cancelled). */
export const OPEN_APPROVAL_STATUSES: readonly string[] = ['pending', 'in_progress'];

/** A pending step is delegated only if `delegated_to` really holds an id (undefined/null/'' are not). */
export function isDelegatedStep(step: Pick<ApprovalStepLike, 'decision' | 'delegated_to'>): boolean {
  return step.decision === 'pending' && !!step.delegated_to;
}

const ACRONYMS: Record<string, string> = { fdd: 'FDD', uat: 'UAT', brd: 'BRD' };

/** The approval's own title; only if it has none, a readable form of its type. */
export function approvalTitle(approval: Pick<ApprovalLike, 'title' | 'entity_type'>): string {
  const title = approval.title?.trim();
  if (title) return title;
  const words = approval.entity_type.replace(/_/g, ' ').trim().split(/\s+/).filter(Boolean);
  const readable = words.map((w) => ACRONYMS[w.toLowerCase()] ?? w).join(' ');
  return readable ? (/approval$/i.test(readable) ? readable : `${readable} approval`) : 'Approval';
}

/** True if `userId` has an active, undecided step on this still-open approval. */
export function isWaitingForUser(approval: ApprovalLike, userId: string): boolean {
  if (!OPEN_APPROVAL_STATUSES.includes(approval.status)) return false;
  return (approval.steps ?? []).some((s) =>
    s.approver_id === userId && s.decision === 'pending' && !!s.activated_at && !s.skipped_at,
  );
}

export interface GroupedApprovals<T extends ApprovalLike> {
  waiting:    T[]; // needs this user's decision now
  inProgress: T[]; // still running, but not waiting on this user
  completed:  T[]; // approved / rejected / cancelled
}

export function groupApprovalsForUser<T extends ApprovalLike>(approvals: T[], userId: string): GroupedApprovals<T> {
  const grouped: GroupedApprovals<T> = { waiting: [], inProgress: [], completed: [] };
  for (const a of approvals) {
    if (isWaitingForUser(a, userId)) grouped.waiting.push(a);
    else if (OPEN_APPROVAL_STATUSES.includes(a.status)) grouped.inProgress.push(a);
    else grouped.completed.push(a);
  }
  return grouped;
}

/** The id of the document an approval is about, if it is about one. */
export function approvalDocumentId(approval: Pick<ApprovalLike, 'document_id' | 'entity_type' | 'entity_id'>): string | null {
  if (approval.document_id) return approval.document_id;
  return approval.entity_type === 'fdd_approval' || approval.entity_type === 'document' ? approval.entity_id : null;
}

export function findApprovalDocument<D extends { id: string }>(
  approval: Pick<ApprovalLike, 'document_id' | 'entity_type' | 'entity_id'>,
  documents: D[],
): D | undefined {
  const id = approvalDocumentId(approval);
  return id ? documents.find((d) => d.id === id) : undefined;
}

/** Where the portal's Documents page opens a given document (and offers a way back). */
export function portalDocumentPath(documentId: string): string {
  return `/portal/fdd?doc=${encodeURIComponent(documentId)}&from=approvals`;
}
